/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from "react";
import { Typography } from "@mui/material";
import { useSearchParams, useLocation } from "react-router-dom";
import Layout from "../../../layout/Layout";
import { attachSeverity, dateFormater } from "../../../utils/commonfunction";
import { pageLocale } from "../../../utils/locale";
import { getParams } from "../../../utils/helpers";
import { METRICS_CONFIG } from "../../../utils/metricsConfig";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "./metrictrendview.scss";
import { getV5MetricHistory } from "../../../services/romReports";

const MetricTrendView = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const metricParam = searchParams.get("metric") || "Stand_to_Sit_Time";
  const { locale } = getParams();
  const isCJK = locale === "ja" || locale === "zh";
  const mv2 = pageLocale("metricsv2");
  const mv2Metrics = pageLocale("metricsv2_metrics");
  const mv2Units = pageLocale("metricsv2_units");
  const mv2AxisLabels = pageLocale("metricsv2_axis_labels");

  // Get metric config from METRICS_CONFIG
  const getMetricConfig = () => {
    return (
      METRICS_CONFIG.find((m) => m.metric === metricParam) || METRICS_CONFIG[2]
    );
  };

  const metricConfig = getMetricConfig();
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch history data
  useEffect(() => {
    let isMounted = true;

    const fetchHistoryData = async () => {
      setIsLoading(true);
      try {
        let data;
        const response = await getV5MetricHistory(
          metricConfig.category,
          metricConfig.apiField,
          10,
        );

        // Use real API when available. Ensure we're setting an array.
        const responseData = response.data?.data?.data;
        data = Array.isArray(responseData) ? responseData : [];

        if (isMounted) {
          setHistoryData(data);
        }
      } catch (error) {
        console.error("Error fetching history data:", error);
        console.error("Error Response:", error.response?.data);
        console.error("Params Used:", { metricParam, url: error.config?.url });

        // Fallback to empty array
        if (isMounted) {
          setHistoryData([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchHistoryData();

    return () => {
      isMounted = false;
    };
  }, [metricParam, metricConfig.category, metricConfig.apiField]);

  const sortedHistoryDesc =
    historyData && historyData.length > 0
      ? [...historyData].sort(
          (a, b) =>
            new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime(),
        )
      : [];

  const latestReading =
    sortedHistoryDesc.length > 0
      ? sortedHistoryDesc[0]
      : { timeStamp: new Date().toISOString(), value: 0 };

  const getReadingValue = (item) => item.value ?? item.time ?? 0;

  // Get range for the metric from server data passed via navigation state.
  // The API delivers entries best→worst so we tag each with a stable
  // severity key — comparing localized `type` strings breaks for non-EN
  // locales (e.g. JA "良好"/"注意"/"要注意").
  const getMetricRange = () => {
    if (location.state?.rangeData && location.state.rangeData.length > 0) {
      const rangeData = location.state.rangeData;
      // Severity is already attached by TugMetricsDetailsV2; re-applying it
      // would corrupt ordering for ascending-sorted ranges (higher-is-better metrics).
      if (rangeData.some((r) => r.severity)) return rangeData;
      return attachSeverity(rangeData);
    }
    return [];
  };

  const range = getMetricRange();

  // Resolve a value to its severity bucket using minMax containment on the
  // already-tagged range[].
  const getEvaluationStatus = (value) => {
    if (!range || value === null || value === undefined) return null;
    const rangeType = range.find((r) => {
      if (r.minMax) {
        return value >= r.minMax[0] && value <= r.minMax[1];
      }
      if (r.cond) {
        return eval(r.cond.replace(/X/g, Number(value.toFixed(2))));
      }
      return false;
    });
    return rangeType?.severity || null;
  };

  const latestStatus = getEvaluationStatus(getReadingValue(latestReading));

  // Get status badge color and text from a stable severity key.
  const getStatusBadgeColor = (severity) => {
    if (severity === "good") return "#11b981";
    if (severity === "average") return "#f59e0b";
    return "#e53e3e";
  };

  const getStatusText = (severity) => {
    if (severity === "good") return mv2?.status_good || "Good";
    if (severity === "average") return mv2?.status_moderate || "Moderate";
    return mv2?.status_poor || "Poor";
  };

  // Format value — round to whole numbers for count-style metrics, otherwise
  // one decimal (two for unitless ratios like gait smoothness).
  const { na } = pageLocale("common") || { na: "N/A" };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return na || "N/A";
    if (!Number.isFinite(Number(value))) return na || "N/A";

    if (metricConfig.unit === "steps/min" || metricConfig.unit === "count") {
      return Math.round(value);
    }
    const num = Number(value);
    if (metricConfig.unit === "unitless") {
      return num % 1 === 0 ? num.toString() : num.toFixed(2);
    }
    return num % 1 === 0 ? num.toString() : num.toFixed(1);
  };

  // Get localized display name; fall back to a humanized metric key.
  const getDisplayName = () =>
    mv2Metrics?.[metricConfig.metric] || metricConfig.metric.replace(/_/g, " ");

  // Get localized unit (e.g. "秒", "cm", "歩/分") for the current metric.
  const getDisplayUnit = () => mv2Units?.[metricConfig.metric] ?? "";

  // Prepare chart data
  const chartData =
    historyData && historyData.length > 0
      ? historyData.map((item) => {
          const val = getReadingValue(item);
          return {
            x: new Date(item.timeStamp).getTime(),
            y: val,
            status: getEvaluationStatus(val),
          };
        })
      : [];

  // Get plot bands for reference zones
  const getPlotBands = () => {
    const bands = [];
    let yMin = 0;
    let yMax = 3;

    range.forEach((r) => {
      if (r.minMax) {
        yMin = Math.min(yMin, r.minMax[0]);
        yMax = Math.max(yMax, r.minMax[1]);
      }
    });

    range.forEach((r) => {
      if (r.minMax) {
        const sev = r.severity;
        const color =
          sev === "good"
            ? "rgba(17, 185, 129, 0.2)"
            : sev === "average"
            ? "rgba(245, 158, 11, 0.2)"
            : "rgba(229, 62, 62, 0.2)";
        const textColor =
          sev === "good"
            ? "#11b981"
            : sev === "average"
            ? "#f59e0b"
            : "#e53e3e";

        bands.push({
          from: r.minMax[0],
          to: r.minMax[1],
          color: color,
          label: {
            text: getStatusText(sev),
            style: {
              color: textColor,
              fontWeight: "600",
              fontSize: "12px",
            },
            align: "left",
            x: 30,
            y: 2,
            verticalAlign: "middle",
          },
        });
      }
    });

    return { bands, yMin, yMax };
  };

  const { bands, yMin, yMax } = getPlotBands();

  // Sort chronologically and prepare equidistant category-based chart data
  const sortedData =
    chartData && chartData.length > 0
      ? [...chartData].sort((a, b) => a.x - b.x)
      : [];
  const categoryLabels = sortedData.map((point) => {
    const d = new Date(point.x);
    if (isCJK) {
      return d.getUTCMonth() + 1 + "月" + d.getUTCDate() + "日";
    }
    return `${d.getDate()} ${d.toLocaleString("default", { month: "short" })}`;
  });
  const seriesData = sortedData.map((point) => point.y);

  const trendTitle = `${getDisplayName()}${mv2?.trend_suffix ?? " Trend"}`;
  const displayUnit = getDisplayUnit();

  if (isLoading) {
    return (
      <Layout>
        <div className="metric-trend-view">
          <div className="trend-header">
            <Typography className="trend-title">{trendTitle}</Typography>
          </div>
          <div style={{ padding: "40px", textAlign: "center" }}>
            <Typography>{mv2?.loading || "Loading..."}</Typography>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="metric-trend-view">
        <div className="trend-header">
          <Typography className="trend-title">{trendTitle}</Typography>
        </div>

        <div className="latest-reading-card">
          <Typography className="latest-reading-label">
            {mv2?.latest_reading || "Latest Reading"} (
            {dateFormater(latestReading.timeStamp)})
          </Typography>
          <div className="latest-reading-content">
            <Typography className="latest-reading-value">
              {formatValue(getReadingValue(latestReading))}
              {displayUnit ? ` ${displayUnit}` : ""}
            </Typography>
            {latestStatus && (
              <div
                className="latest-reading-badge"
                style={{
                  backgroundColor: getStatusBadgeColor(latestStatus),
                }}
              >
                {getStatusText(latestStatus)}
              </div>
            )}
          </div>
        </div>

        <div className="trend-over-time-card">
          <Typography className="trend-over-time-title">
            {mv2?.trend_over_time || "Trend Over Time"}
          </Typography>
          <div className="chart-container">
            {historyData && historyData.length > 0 && seriesData.length > 0 ? (
              <HighchartsReact
                highcharts={Highcharts}
                options={{
                  chart: {
                    type: "line",
                    height: 350,
                    // Only force a scrollable plot area once there are enough
                    // points to actually need it — otherwise a single reading
                    // gets clipped and forces the user to scroll horizontally.
                    ...(seriesData.length > 5
                      ? {
                          scrollablePlotArea: {
                            minWidth: 700,
                            scrollPositionX: 1,
                          },
                        }
                      : {}),
                  },
                  title: {
                    text: null,
                  },
                  xAxis: {
                    categories: categoryLabels,
                    crosshair: true,
                  },
                  yAxis: {
                    // Per-metric axis label from locale (e.g. "Speed (cm/s)",
                    // "Step Count/min") rather than a generic "Time (unit)".
                    title: {
                      text:
                        mv2AxisLabels?.[metricConfig.metric] ||
                        (displayUnit
                          ? `${getDisplayName()} (${displayUnit})`
                          : getDisplayName()),
                    },
                    min: yMin,
                    max: yMax,
                    plotBands: bands,
                  },
                  series: [
                    {
                      name: getDisplayName(),
                      data: seriesData,
                    },
                  ],
                  credits: {
                    enabled: false,
                  },
                }}
              />
            ) : (
              <Typography>
                {mv2?.no_data_available || "No data available"}
              </Typography>
            )}
          </div>
        </div>

        <div className="history-log-section">
          <Typography className="history-log-title">
            {mv2?.history_log || "History Log"}
          </Typography>
          <div className="history-log-list">
            {sortedHistoryDesc && sortedHistoryDesc.length > 0 ? (
              sortedHistoryDesc.map((item, index) => {
                const val = getReadingValue(item);
                const status = getEvaluationStatus(val);
                return (
                  <div key={index} className="history-log-item">
                    <Typography className="history-log-date">
                      {dateFormater(item.timeStamp)}
                    </Typography>
                    <Typography className="history-log-value">
                      | {formatValue(val)}
                      {displayUnit ? ` ${displayUnit}` : ""}
                    </Typography>
                    {status && (
                      <div
                        className="history-log-badge"
                        style={{
                          backgroundColor: getStatusBadgeColor(status),
                        }}
                      >
                        {getStatusText(status)}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <Typography>
                {mv2?.no_history_data_available || "No history data available"}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MetricTrendView;
